import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { PageSection, pdpSectionStyle } from 'common/components/page-section';
import { connect } from 'react-redux';
import {
  getInfo,
  getPrimaryPrice,
  isProductSet,
  getProductBelong,
  getProductMarketingDescription,
} from 'features/product-info';
import ReactMarkdown from 'react-markdown';
import { getSectionMeta } from 'features/page-navigation/ducks/selectors';
import { Sections } from 'features/page-navigation/components/section-refs-context';
import { RootReducer } from 'store/root-reducer';
import {
  doesItHaveBrandsPage,
  getBrandLogoImgSrc,
  getBrandPageLink,
  getBrandTitle,
} from 'features/page-model/ducks/get-model';
import { SectionHeader, BrandBadge } from 'common/components';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { MediaQueries } from 'utils/internal-specifics/media-queries';
import { cn } from 'utils';
import { Breakpoint } from 'types/utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { D9nSectionMappedState, D9nSectionProps } from './types';
import { mdRenderers } from './md-renders';
import { useOptimisedImages } from '@navi-toolbox/hooks';

export const DATA_QA_D9N_SECTION = 'content-d9n';

type Props = D9nSectionProps & { breakpoint: Breakpoint };

/**
 * @internal Used only for tests
 */
export const D9nSection: FC<Props> = ({
  breakpoint,
  contentSource,
  sectionMeta,
  id,
  showBrandTile,
  brandTitle,
  brandPageLink,
  brandLogo = '',
  className,
  onCollapsedToggle,
  isSet,
}) => {
  const ref = useContext(Sections.Context).get('description');

  const isDesktop = breakpoint === 'mobile' || breakpoint === 'tablet';
  const brandBadgeVariant = isDesktop ? 'thin' : 'general';
  const sectionId = sectionMeta?.navigationId || id;
  const optimisedBrandLogo = useOptimisedImages({
    src: brandLogo,
    sizes: {
      default: {
        w: '130',
        h: '60',
      },
    },
  });

  const sendAnalytics = useCallback((isFade: boolean) => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: isSet ? 'komplektyDetail' : 'productDetail',
      elementName: `description / ${isFade ? 'close' : 'open'}`,
      elementText: 'Описание',
      elementType: 'button',
    });
  }, []);
  // todo: make sure that brand logo is not used and remove

  if (sectionMeta) {
    return (
      <section id={sectionId} className={cn(pdpSectionStyle, className)} data-qa={DATA_QA_D9N_SECTION}>
        <PageSection
          onSendAnalytics={sendAnalytics}
          onCollapsedToggle={onCollapsedToggle}
          sectionLocation="d9n"
          variant="v-limited"
          headline={<SectionHeader ref={ref}>{sectionMeta.title}</SectionHeader>}
          sideContent={
            showBrandTile && (
              <a href={brandPageLink} data-qa="brand-badge_mf-pdp">
                <BrandBadge
                  title={brandTitle}
                  logoImg={optimisedBrandLogo.default.x || ''}
                  variant={brandBadgeVariant}
                />
              </a>
            )
          }
        >
          {/* eslint-disable react/no-children-prop */}
          <div>
            <ReactMarkdown components={mdRenderers} children={contentSource || ''} />
          </div>
        </PageSection>
      </section>
    );
  }
  return null;
};

const D9nSectionWithMediaEffect: FC<D9nSectionProps> = ({ ...props }) => {
  // TODO: Extract to common context
  // https://jira.lmru.tech/browse/MF-895
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('large');

  useEffect(
    () =>
      addMediaQueryHandlers(
        [
          MediaQueries.LARGE_DESK_SCREEN,
          MediaQueries.MEDIUM_DESK_SCREEN,
          MediaQueries.TABLET_SCREEN,
          MediaQueries.MOBILE_SCREEN,
        ],
        ['large', 'medium', 'tablet', 'mobile'],
        'large',
        setBreakpoint,
      ),
    [],
  );
  return <D9nSection breakpoint={breakpoint} {...props} />;
};

export const mapStateToProps = (state: RootReducer): D9nSectionMappedState => {
  const contentSource = getProductMarketingDescription(state) ?? null;
  const sectionMeta = getSectionMeta('d9n')(state) ?? null;
  const showBrandTile = doesItHaveBrandsPage(state);
  const brandTitle = getBrandTitle(state) ?? '';
  const brandPageLink = getBrandPageLink(state) ?? '';
  const productInfo = getInfo(state);
  const productPrice = getPrimaryPrice(state)?.price;
  const productBelong = getProductBelong(state);
  const brandLogo = getBrandLogoImgSrc(state);
  const isSet = isProductSet(state);

  return {
    contentSource,
    sectionMeta,
    showBrandTile,
    brandTitle,
    brandPageLink,
    brandLogo,
    productId: productInfo?.productId,
    productName: productInfo?.displayedName,
    productPrice,
    productBelong,
    isSet,
  };
};

export const PdpD9nSection = connect(mapStateToProps)(D9nSectionWithMediaEffect);
